import { BenefitType } from '@zorro/clients';
import { isDefined } from '@zorro/common-utils';
import { getBenefitTypeConfig } from '@zorro/web/ui-utils';

export const BENEFIT_TYPE_ORDER = [
  BenefitType.MAJOR_MEDICAL,
  BenefitType.SUPPLEMENTAL_DENTAL,
  BenefitType.SUPPLEMENTAL_VISION,
] as const;

export const DENTAL_TYPES = [
  BenefitType.SUPPLEMENTAL_DENTAL,
  BenefitType.SUPPLEMENTAL_COMBINED_DENTAL_VISION,
] as const;

export type PlanLine = { carrierName: string | null; planName: string | null };

type BenefitPlanEntry = PlanLine & { benefitType: BenefitType };

type BenefitMetadata = {
  plan?: PlanLine;
  plans?: BenefitPlanEntry[];
  clearedPlans?: BenefitPlanEntry[];
};

export function normalizeType(type: BenefitType): BenefitType {
  return type === BenefitType.SUPPLEMENTAL_COMBINED_DENTAL_VISION
    ? BenefitType.SUPPLEMENTAL_DENTAL
    : type;
}

function isBenefitMetadata(
  value: Record<string, unknown>
): value is BenefitMetadata {
  if (
    isDefined(value.plan) &&
    (typeof value.plan !== 'object' || Array.isArray(value.plan))
  )
    return false;
  if (isDefined(value.plans) && !Array.isArray(value.plans)) return false;
  if (isDefined(value.clearedPlans) && !Array.isArray(value.clearedPlans))
    return false;
  return true;
}

export function getPlansForTypes(
  metadata: Record<string, unknown> | null | undefined,
  benefitTypes: readonly BenefitType[]
): PlanLine[] {
  if (!metadata || !isBenefitMetadata(metadata)) return [];

  const plans = metadata.plans ?? metadata.clearedPlans;
  if (plans) {
    return plans
      .filter((plan) => benefitTypes.includes(plan.benefitType))
      .map((plan) => ({
        carrierName: plan.carrierName ?? null,
        planName: plan.planName ?? null,
      }));
  }

  if (metadata.plan) {
    return [
      {
        carrierName: metadata.plan.carrierName ?? null,
        planName: metadata.plan.planName ?? null,
      },
    ];
  }

  return [];
}

export function buildBadges(
  benefitTypes: BenefitType[],
  metadata: Record<string, unknown> | null | undefined
) {
  const typeCounts = benefitTypes.reduce((acc, type) => {
    const normalized = normalizeType(type);
    if (getBenefitTypeConfig(normalized)) {
      acc.set(normalized, (acc.get(normalized) ?? 0) + 1);
    }
    return acc;
  }, new Map<BenefitType, number>());

  if (typeCounts.size === 0) return null;

  const prioritized = new Set<BenefitType>(BENEFIT_TYPE_ORDER);
  const orderedTypes = [
    ...BENEFIT_TYPE_ORDER,
    ...[...typeCounts.keys()].filter((type) => !prioritized.has(type)),
  ];

  return orderedTypes.flatMap((type) => {
    const count = typeCounts.get(type);
    const config = getBenefitTypeConfig(type);
    if (!count || !config) return [];
    const queryTypes =
      type === BenefitType.SUPPLEMENTAL_DENTAL ? DENTAL_TYPES : [type];
    return [
      {
        type,
        count,
        config,
        planLines: getPlansForTypes(metadata, queryTypes),
      },
    ];
  });
}
